import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerCloseButton,
  DrawerBody,
  Button,
  VStack,
  Flex,
  Text,
  Box,
  IconButton,
  Center,
  Spinner,
} from "@chakra-ui/react";
import { FolderIcon } from "@root/assets/icons/foldericon";
import { useTranslation } from "react-i18next";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useEffect } from "react";
import { Paginate } from "../pagination";
import { PAGINATION_DEFAULTS } from "@root/constants";
import { EntityListItem } from "./EntityListItem";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  folders: any[];
  getFolders: (payload: { projectId: number }) => void;
  onFolderClick: (folder: { id: string; name: string }) => void;
  onCreateClick: () => void;
  projects: Project[];
  setSelectedProject: any;
  selectedProject: any;
  folderCount: number;
  folderPage: number;
  setFolderPage: (page: number) => void;
  projectPage: number;
  setProjectPage: (page: number) => void;
  projectCount: number;
  onDeleteFolder: (folder: FolderData) => void;
  onDeleteProject: (project: Project) => void;
  onEditFolder: (folder: FolderData) => void;
  onEditProject: (project: Project) => void;
  foldersLoading?: boolean;
  projectsLoading?: boolean;
}

const FolderDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  setSelectedProject,
  folders,
  onFolderClick,
  onCreateClick,
  onDeleteFolder,
  onDeleteProject,
  onEditFolder,
  onEditProject,
  projects,
  getFolders,
  selectedProject,
  folderCount,
  folderPage,
  setFolderPage,
  projectPage,
  setProjectPage,
  projectCount,
  foldersLoading,
  projectsLoading,
}) => {
  const { t } = useTranslation("common");

  useEffect(() => {
    if (selectedProject?.id) {
      const payload = {
        projectId: selectedProject.id,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        page: folderPage,
      };
      getFolders(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, folderPage]); // Only depend on project ID, not whole object

  const handleFolderPageChange = (pageNumber: number) => {
    setFolderPage(pageNumber);
  };

  const handleProjectPageChange = (pageNumber: number) => {
    setProjectPage(pageNumber);
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setFolderPage(1); // Reset to page 1 when selecting new project
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setFolderPage(1);
  };

  const isProjectsView = !selectedProject;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="sm">
      <DrawerOverlay />
      <DrawerContent bg="white" height="100vh" overflow="hidden">
        <DrawerCloseButton />
        <DrawerHeader
          fontWeight="semibold"
          fontSize="md"
          borderBottomWidth="1px"
          borderColor="gray.200"
          flexShrink={0}
          py={3}
          px={4}
        >
          <Flex align="center" gap={2}>
            {!isProjectsView && (
              <IconButton
                aria-label={t("projects.backToProjects")}
                icon={<ArrowBackIcon />}
                size="sm"
                variant="ghost"
                p={1.5}
                onClick={handleBackToProjects}
              />
            )}
            <Text flex="1" fontSize="md">
              {selectedProject
                ? `${selectedProject.name} - ${t("projects.folders")}`
                : t("admin.fileManagement")}
            </Text>
          </Flex>
        </DrawerHeader>

        <DrawerBody
          display="flex"
          flexDirection="column"
          p={0}
          overflow="hidden"
        >
          {/* Scrollable list area */}
          <Box
            flex="1"
            minH={0}
            overflowY="scroll"
            p={2}
            display="flex"
            flexDirection="column"
            onWheel={(e) => e.stopPropagation()}
            sx={{
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              touchAction: "pan-y",
              "&::-webkit-scrollbar": {
                width: "2px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#F1F1F1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#A0AEC0",
                borderRadius: "4px",
                "&:hover": {
                  background: "#718096",
                },
              },
            }}
          >
            <Text fontSize="xs" mb={1.5} color="gray.500">
              {isProjectsView ? t("projects.projects") : t("projects.folders")}
            </Text>

            <Box flex="1" display="flex" flexDirection="column">
              {isProjectsView ? (
                // Projects View
                projectsLoading ? (
                  <Center flex="1" minH="150px">
                    <Spinner size="sm" thickness="2px" />
                  </Center>
                ) : (
                  <>
                    {projects &&
                    Array.isArray(projects) &&
                    projects.length > 0 ? (
                      <VStack align="stretch" spacing={2}>
                        {projects.map((project) => (
                          <EntityListItem
                            key={project.id}
                            id={project.id}
                            name={project.name}
                            icon={FolderIcon}
                            onItemClick={() => handleProjectClick(project)}
                            onEdit={() => onEditProject(project)}
                            onDelete={() => onDeleteProject(project)}
                          />
                        ))}
                      </VStack>
                    ) : !projectsLoading ? (
                      <Center flex="1" minH="150px">
                        <Text fontSize="xs" color="gray.500" textAlign="center">
                          {t("projects.noProjectsFound")}
                        </Text>
                      </Center>
                    ) : null}
                  </>
                )
              ) : // Folders View
              foldersLoading ? (
                <Center flex="1" minH="150px">
                  <Spinner size="sm" thickness="2px" />
                </Center>
              ) : (
                <>
                  {folders && Array.isArray(folders) && folders.length > 0 ? (
                    <VStack align="stretch" spacing={2}>
                      {folders.map((folder) => (
                        <EntityListItem
                          key={folder.id}
                          id={folder.id}
                          name={folder.name}
                          icon={FolderIcon}
                          onItemClick={() => onFolderClick(folder)}
                          onEdit={() => onEditFolder(folder)}
                          onDelete={() => onDeleteFolder(folder)}
                        />
                      ))}
                    </VStack>
                  ) : !foldersLoading ? (
                    <Center flex="1" minH="150px">
                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        {t("userManagement.noFolders")}
                      </Text>
                    </Center>
                  ) : null}
                </>
              )}
            </Box>
          </Box>

          {/* Fixed footer area - always at bottom */}
          <Box
            flexShrink={0}
            borderTopWidth="1px"
            borderColor="gray.200"
            p={2}
            bg="white"
          >
            <form
              onSubmit={(e) => {
                e.preventDefault(); // prevent page reload
                onCreateClick(); // trigger create
              }}
            >
              <Flex direction="column" gap={2}>
                {/* Create button → right */}
                <Flex justify="flex-end">
                  <Button
                    type="submit"
                    colorScheme="blue"
                    leftIcon={<FolderIcon />}
                    bg="#0E0E0E"
                    _hover={{ bg: "#2D2D2D" }}
                    size="sm"
                    fontSize="xs"
                    height="32px"
                    px={3}
                  >
                    {isProjectsView
                      ? t("projects.createProject")
                      : t("projects.createFolder")}
                  </Button>
                </Flex>

                {/* Pagination → center */}
                <Flex justify="center">
                  <Paginate
                    page={isProjectsView ? projectPage : folderPage}
                    count={isProjectsView ? projectCount : folderCount}
                    pageSize={PAGINATION_DEFAULTS.PAGESIZE}
                    onPageChange={
                      isProjectsView
                        ? handleProjectPageChange
                        : handleFolderPageChange
                    }
                  />
                </Flex>
              </Flex>
            </form>
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default FolderDrawer;
